/* ═══════════════════════════════════════════════════════════
   Listing pages — the lightbox.

   Entry point for dist/js/category.js, used by the category, artist, search
   and wishlist pages. It pulls in app.js, which does the header, the menus
   and the endless scrolling, so the page loads a single script.

   Everything here is enhancement on top of what the server sent. The grid is
   rendered server-side and every tile is a plain link to its detail page; the
   paging underneath it is a list of real <a href>s (PLAN.md section 2.6).
   Without this file you land on the detail page instead of opening the photo
   in place — which is also what a crawler does.

   The orientation filter stays server-side on purpose: it narrows the query,
   not the page, so it keeps working across all of the pages and Google can
   follow it.
   ═══════════════════════════════════════════════════════════ */
import './app.js';
import { landOn } from './modal.js';

(() => {
  'use strict';

  const list = document.querySelector('[data-objects]');
  /* `data-photo-viewer` and not `[data-modal]`: that attribute belongs to
     modal.js, so the first window the page happens to carry - the search,
     the video - was handed to the viewer, which then found none of its own
     parts and threw on every tile. See _lightbox.php. */
  const modal = document.querySelector('[data-photo-viewer]');

  if (!list) return;

  /* The tiles are the model: whatever is on the page right now, in the order
     it is in. Endless scrolling adds to it, so this is re-read after every
     batch instead of captured once. */
  let tiles = [...list.querySelectorAll('[data-open]')];

  const refresh = () => {
    tiles = [...list.querySelectorAll('[data-open]')];
  };

  /* The endless scrolling itself is in app.js — the customer reactions page
     runs the same thing — and it says so when a batch has landed. */
  list.addEventListener('endless:batch', refresh);

  /* ── lightbox ── */

  if (!modal) return;

  const image = modal.querySelector('[data-modal-img]');
  const number = modal.querySelector('[data-modal-nr]');
  const text = modal.querySelector('[data-modal-text]');
  const counter = modal.querySelector('[data-modal-counter]');
  const favorite = modal.querySelector('[data-modal-favorite]');
  const link = modal.querySelector('[data-modal-link]');

  let current = 0;

  const show = (index) => {
    current = (index + tiles.length) % tiles.length;

    const tile = tiles[current];

    image.src = tile.dataset.image;
    image.alt = tile.dataset.name;

    /* The dimensions of the source, when the cms has them (PLAN.md 1.6).
       They fix the shape of the box before the photo arrives, so the dialog
       does not jump on every step. */
    if (tile.dataset.width && tile.dataset.height) {
      image.width = tile.dataset.width;
      image.height = tile.dataset.height;
    } else {
      image.removeAttribute('width');
      image.removeAttribute('height');
    }

    number.textContent = tile.dataset.number;

    /* Only a photograph with a name of its own gets a title here. A nameless
       one carries its order code as its name, and that code already stands in
       the corner of this window. Left empty and not hidden: the bar is a
       `justify-between`, so with the title gone the buttons would slide from
       the right of the window to the left. */
    text.textContent = tile.dataset.title ?? '';
    counter.textContent = `${current + 1} / ${tiles.length}`;
    link.href = tile.href;

    /* The heart in the dialog and the one on the tile are the same switch, so
       it has to open in the state that tile is in. */
    const button = tile.closest('figure')?.querySelector('[data-favorite]');

    favorite.dataset.for = button ? button.dataset.favorite : '';
    favorite.setAttribute('aria-pressed', button?.getAttribute('aria-pressed') ?? 'false');
  };

  const open = (index) => {
    show(index);
    modal.classList.remove('hidden');
    document.documentElement.classList.add('overflow-hidden');
    modal.querySelector('[data-modal-close]').focus();
  };

  /* Closing lands on the photograph the window was showing, not the one it
     was opened from — see landOn() in modal.js. */
  const close = () => {
    modal.classList.add('hidden');
    document.documentElement.classList.remove('overflow-hidden');
    landOn(tiles[current]);
  };

  /* One handler for the whole grid, so a listing of six hundred tiles still
     costs one listener. */
  list.addEventListener('click', (event) => {
    const tile = event.target.closest('[data-open]');

    if (!tile) return;

    /* Anything but a plain left click is someone who wants the detail page:
       a new tab, a bookmark, a middle click. Let the link be a link. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    open(tiles.indexOf(tile));
  });

  modal.querySelector('[data-modal-close]').addEventListener('click', close);
  modal.querySelector('[data-modal-prev]').addEventListener('click', () => show(current - 1));
  modal.querySelector('[data-modal-next]').addEventListener('click', () => show(current + 1));

  /* Clicking beside the photo closes it; on the photo or the buttons it does
     not. */
  modal.addEventListener('click', (event) => {
    if (event.target === modal || event.target.dataset.modalArea !== undefined) close();
  });

  document.addEventListener('keydown', (event) => {
    if (modal.classList.contains('hidden')) return;

    if (event.key === 'Escape') close();
    if (event.key === 'ArrowLeft') show(current - 1);
    if (event.key === 'ArrowRight') show(current + 1);
  });

  /* Swiping. 40 px is enough to tell a swipe from a tap, and the movement has
     to be mostly horizontal — otherwise someone is scrolling. */
  const area = modal.querySelector('[data-modal-area]');
  let startX = 0;
  let startY = 0;

  area.addEventListener(
    'touchstart',
    (event) => {
      startX = event.changedTouches[0].clientX;
      startY = event.changedTouches[0].clientY;
    },
    { passive: true }
  );

  area.addEventListener(
    'touchend',
    (event) => {
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;

      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(current + (dx < 0 ? 1 : -1));
    },
    { passive: true }
  );
})();
